import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import Header from '@/components/layout/Header';
import Footer from '@/components/layout/Footer';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { ThumbsUp, CheckCircle2, MessageSquare, Edit, Trash2, Send, ExternalLink, ArrowLeft, Reply, X } from 'lucide-react';
import TipTapEditor from '@/components/TipTapEditor';
import { useAuth } from '@/context/AuthContext';
import { showError, showSuccess } from '@/utils/toast';
import {
  GameRequestComment,
  GameRequestDetail,
  extractErrorMessage,
  formatGameRequestDate,
  getCsrfToken,
} from '@/utils/gameRequests';
import GameRequestAccessGate from '@/components/GameRequestAccessGate';

const jsonHeaders = (): HeadersInit => ({
  'X-CSRF-Token': getCsrfToken(),
  'Content-Type': 'application/json',
  Accept: 'application/json',
});

const GameRequestShow: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { isLoggedIn, currentUser, isAdmin, isRealModerator, isMuted, loading: authLoading } = useAuth();
  const canAccess = isLoggedIn && !!currentUser?.name;

  const [request, setRequest] = useState<GameRequestDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  const [replyText, setReplyText] = useState('');
  const [submittingReply, setSubmittingReply] = useState(false);
  const [quotedComment, setQuotedComment] = useState<GameRequestComment | null>(null);

  const [editingId, setEditingId] = useState<number | null>(null);
  const [editingText, setEditingText] = useState('');

  const [editingRequest, setEditingRequest] = useState(false);
  const [editName, setEditName] = useState('');
  const [editOtherLink, setEditOtherLink] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [savingRequest, setSavingRequest] = useState(false);

  const loadRequest = useCallback(async () => {
    try {
      const response = await fetch(`/api/game_requests/${id}`, { headers: { Accept: 'application/json' } });
      if (response.status === 404) {
        setNotFound(true);
        return;
      }
      if (!response.ok) throw new Error('load failed');
      setRequest(await response.json());
    } catch {
      showError('Nem sikerült betölteni a kérést.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    if (canAccess) loadRequest();
  }, [canAccess, loadRequest]);

  const handleVote = async () => {
    if (!isLoggedIn) {
      showError('A szavazáshoz be kell jelentkezned.');
      return;
    }
    try {
      const response = await fetch(`/api/game_requests/${id}/vote`, { method: 'POST', headers: jsonHeaders() });
      if (!response.ok) throw new Error('vote failed');
      await loadRequest();
    } catch {
      showError('Nem sikerült a szavazás.');
    }
  };

  const handleComplete = async () => {
    try {
      const response = await fetch(`/api/game_requests/${id}/complete`, { method: 'POST', headers: jsonHeaders() });
      if (!response.ok) throw new Error('complete failed');
      await loadRequest();
    } catch {
      showError('Nem sikerült módosítani az állapotot.');
    }
  };

  const handleReply = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoggedIn) {
      showError('A hozzászóláshoz be kell jelentkezned.');
      return;
    }
    setSubmittingReply(true);
    try {
      const response = await fetch(`/api/game_requests/${id}/comments`, {
        method: 'POST',
        headers: jsonHeaders(),
        body: JSON.stringify({ game_request_comment: { content: replyText, quoted_comment_id: quotedComment?.id ?? null } }),
      });
      if (!response.ok) {
        showError(await extractErrorMessage(response, 'Nem sikerült elküldeni a hozzászólást.'));
        return;
      }
      setReplyText('');
      setQuotedComment(null);
      showSuccess('Hozzászólás elküldve.');
      await loadRequest();
    } catch {
      showError('Hálózati hiba történt.');
    } finally {
      setSubmittingReply(false);
    }
  };

  const scrollToElement = (elementId: string) => {
    document.getElementById(elementId)?.scrollIntoView({ behavior: 'smooth', block: 'center' });
  };

  const startQuote = (comment: GameRequestComment) => {
    setQuotedComment(comment);
    scrollToElement('reply-form');
  };

  const startEdit = (comment: GameRequestComment) => {
    setEditingId(comment.id);
    setEditingText(comment.content_html);
  };

  const saveEdit = async (commentId: number) => {
    try {
      const response = await fetch(`/api/game_request_comments/${commentId}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({ game_request_comment: { content: editingText } }),
      });
      if (!response.ok) {
        showError(await extractErrorMessage(response, 'Nem sikerült menteni a módosítást.'));
        return;
      }
      setEditingId(null);
      await loadRequest();
    } catch {
      showError('Hálózati hiba történt.');
    }
  };

  const startEditRequest = () => {
    if (!request) return;
    setEditName(request.game_name);
    setEditOtherLink(request.other_link ?? '');
    setEditDescription(request.description_html ?? '');
    setEditingRequest(true);
  };

  const saveRequestEdit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSavingRequest(true);
    try {
      const response = await fetch(`/api/game_requests/${id}`, {
        method: 'PATCH',
        headers: jsonHeaders(),
        body: JSON.stringify({
          game_request: {
            game_name: editName.trim(),
            other_link: editOtherLink.trim(),
            description: editDescription,
          },
        }),
      });
      if (!response.ok) {
        showError(await extractErrorMessage(response, 'Nem sikerült menteni a kérést.'));
        return;
      }
      setEditingRequest(false);
      await loadRequest();
    } catch {
      showError('Hálózati hiba történt.');
    } finally {
      setSavingRequest(false);
    }
  };

  const deleteRequest = async () => {
    if (!window.confirm('Biztosan törlöd ezt a kérést a hozzászólásaival együtt?')) return;
    try {
      const response = await fetch(`/api/game_requests/${id}`, {
        method: 'DELETE',
        headers: jsonHeaders(),
      });
      if (!response.ok) {
        showError(await extractErrorMessage(response, 'Nem sikerült törölni a kérést.'));
        return;
      }
      showSuccess('A kérés törölve.');
      navigate('/keres');
    } catch {
      showError('Hálózati hiba történt.');
    }
  };

  const deleteComment = async (commentId: number) => {
    if (!window.confirm('Biztosan törlöd a hozzászólást?')) return;
    try {
      const response = await fetch(`/api/game_request_comments/${commentId}`, {
        method: 'DELETE',
        headers: jsonHeaders(),
      });
      if (!response.ok) throw new Error('delete failed');
      await loadRequest();
    } catch {
      showError('Nem sikerült törölni a hozzászólást.');
    }
  };

  if (authLoading) {
    return <div className="min-h-screen flex items-center justify-center text-muted-foreground">Betöltés...</div>;
  }

  if (!canAccess) {
    return <GameRequestAccessGate needsLogin={!isLoggedIn} />;
  }

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto p-4 py-8">
          <p className="text-center text-muted-foreground py-10">Betöltés...</p>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !request) {
    return (
      <div className="min-h-screen flex flex-col">
        <Header />
        <main className="flex-grow container mx-auto p-4 py-8 text-center">
          <p className="text-muted-foreground py-10">A kérés nem található.</p>
          <Link to="/keres" className="text-primary hover:underline">Vissza a kérésekhez</Link>
        </main>
        <Footer />
      </div>
    );
  }

  const requester = request.user;

  return (
    <div className="min-h-screen flex flex-col">
      <div className="sticky top-0 z-50 w-full">
        <Header />
      </div>
      <main className="flex-grow container mx-auto p-4 py-8 main-content-bg mt-[12px] max-w-4xl space-y-6">
        <Link to="/keres" className="inline-flex items-center gap-2 text-primary hover:underline">
          <ArrowLeft className="h-4 w-4" /> Vissza a kérésekhez
        </Link>

        {/* Első hozzászólás: maga a kérés */}
        <Card className="bg-card text-card-foreground border-border shadow-md overflow-hidden">
          <div className="flex flex-col sm:flex-row">
            <div className="w-full sm:w-48 bg-secondary p-4 flex flex-col items-center text-center border-b sm:border-b-0 sm:border-r border-border">
              <Avatar className="w-20 h-20 mb-2 border-2 border-primary">
                <AvatarImage src={requester?.avatar_url ?? undefined} alt={requester?.username} className="object-cover" />
                <AvatarFallback className="bg-primary text-primary-foreground text-2xl">
                  {requester?.username?.charAt(0).toUpperCase() ?? '?'}
                </AvatarFallback>
              </Avatar>
              <p className="font-bold text-foreground">{requester?.username ?? 'Ismeretlen'}</p>
              {requester?.is_admin && <Badge className="bg-red-600 text-white mt-1">Admin</Badge>}
              <p className="text-xs text-muted-foreground mt-2">{formatGameRequestDate(request.created_at)}</p>
            </div>

            <div className="flex-1 p-6 min-w-0">
              {editingRequest ? (
                <form onSubmit={saveRequestEdit} className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="edit-game-name">Játék neve</Label>
                    <Input
                      id="edit-game-name"
                      value={editName}
                      onChange={e => setEditName(e.target.value)}
                      maxLength={255}
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="edit-other-link">Egyéb link</Label>
                    <Input
                      id="edit-other-link"
                      value={editOtherLink}
                      onChange={e => setEditOtherLink(e.target.value)}
                      placeholder="https://..."
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Leírás</Label>
                    <TipTapEditor value={editDescription} onChange={setEditDescription} />
                  </div>
                  <div className="flex justify-end gap-2">
                    <Button type="button" variant="ghost" size="sm" onClick={() => setEditingRequest(false)}>
                      Mégse
                    </Button>
                    <Button type="submit" size="sm" disabled={savingRequest || !editName.trim()}>
                      Mentés
                    </Button>
                  </div>
                </form>
              ) : (
                <>
                <CardHeader className="p-0 mb-4">

                  <div className="flex flex-wrap items-center gap-2">
                    <CardTitle className="text-2xl">{request.game_name}</CardTitle>
                    <Badge variant="secondary">{request.request_type === 'update_request' ? 'Frissítés' : 'Játék kérés'}</Badge>
                    {request.completed && <Badge className="bg-green-600 text-white">Elkészült</Badge>}
                    {request.game && (
                      <Link to={request.game.path} className="text-sm font-semibold text-primary hover:underline inline-flex items-center gap-1">
                        Játék oldala <ExternalLink className="h-3 w-3" />
                      </Link>
                    )}
                  </div>
                </CardHeader>

                {request.image_url && (
                  <img
                    src={request.image_url}
                    alt={request.steam_name ?? request.game_name}
                    className="w-full max-w-md rounded mb-4"
                    loading="lazy"
                  />
                )}

                {request.steam_link && (
                  <a
                    href={request.steam_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline mb-4"
                  >
                    <ExternalLink className="h-4 w-4" /> Steam oldal
                  </a>
                )}

                {request.other_link && (
                  <a
                    href={request.other_link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-primary hover:underline mb-4 break-all"
                  >
                    <ExternalLink className="h-4 w-4 shrink-0" /> Egyéb link
                  </a>
                )}

                {(request.platforms.length > 0 || request.version) && (
                  <div className="mb-4 space-y-2">
                    {request.platforms.length > 0 && (
                      <div className="flex flex-wrap gap-1">
                        {request.platforms.map(platform => (
                          <Badge key={platform} variant="outline">{platform}</Badge>
                        ))}
                      </div>
                    )}
                    {request.version && <p className="text-sm text-muted-foreground">Verzió: {request.version}</p>}
                  </div>
                )}

                {request.description_html && (
                  <div
                    className="prose prose-md prose-invert max-w-none break-words mb-4"
                    dangerouslySetInnerHTML={{ __html: request.description_html }}
                  />
                )}

                <div className="flex items-center gap-3 pt-2 border-t border-border">
                  <Button variant={request.voted ? 'default' : 'outline'} size="sm" onClick={handleVote}>
                    <ThumbsUp className="h-4 w-4 mr-1" /> {request.votes_count}
                  </Button>
                  <span className="flex items-center gap-1 text-sm text-muted-foreground">
                    <MessageSquare className="h-4 w-4" /> {request.comments_count}
                  </span>
                  {isAdmin && (
                    <Button variant="ghost" size="sm" onClick={handleComplete}>
                      <CheckCircle2 className="h-4 w-4 mr-1" />
                      {request.completed ? 'Visszanyit' : 'Elkészültnek jelöl'}
                    </Button>
                  )}
                  {isAdmin && (
                    <Button variant="ghost" size="sm" onClick={startEditRequest}>
                      <Edit className="h-4 w-4 mr-1" /> Szerkesztés
                    </Button>
                  )}
                  {(isAdmin || isRealModerator) && (
                    <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-400" onClick={deleteRequest}>
                      <Trash2 className="h-4 w-4 mr-1" /> Törlés
                    </Button>
                  )}
                </div>
                </>
              )}
            </div>
          </div>
        </Card>

        {/* Hozzászólások */}
        <section className="space-y-4">
          <h2 className="text-xl font-semibold">Hozzászólások ({request.comments.length})</h2>

          {request.comments.map(comment => {
            const commentUser = comment.user;
            const isOwn = currentUser && String(currentUser.id) === commentUser.id;
            const canModify = isAdmin || !!isOwn;
            const isEditing = editingId === comment.id;
            const canReply = isLoggedIn && !isMuted;

            return (
              <Card key={comment.id} id={`comment-${comment.id}`} className="bg-card text-card-foreground border-border shadow-md overflow-hidden">
                <div className="flex flex-col sm:flex-row">
                  <div className="w-full sm:w-48 bg-secondary p-4 flex flex-col items-center text-center border-b sm:border-b-0 sm:border-r border-border">
                    <Avatar className="w-16 h-16 mb-2 border-2 border-primary">
                      <AvatarImage src={commentUser.avatar_url ?? undefined} alt={commentUser.username} className="object-cover" />
                      <AvatarFallback className="bg-primary text-primary-foreground text-xl">
                        {commentUser.username?.charAt(0).toUpperCase() ?? '?'}
                      </AvatarFallback>
                    </Avatar>
                    <p className="font-bold text-foreground">{commentUser.username}</p>
                    {commentUser.is_admin && <Badge className="bg-red-600 text-white mt-1">Admin</Badge>}
                  </div>

                  <div className="flex-1 p-4 min-w-0">
                    <div className="text-xs text-muted-foreground mb-2">
                      {formatGameRequestDate(comment.created_at)}
                      {comment.edited && ' (szerkesztve)'}
                    </div>

                    {comment.quoted && !isEditing && (
                      <button
                        type="button"
                        onClick={() => scrollToElement(`comment-${comment.quoted?.id}`)}
                        className="w-full text-left border-l-4 border-primary bg-secondary/50 rounded p-2 mb-3 text-sm"
                      >
                        <span className="text-muted-foreground">
                          Idézet <span className="text-primary">{comment.quoted.username}</span> hozzászólásából:
                        </span>
                        <p className="italic break-words text-foreground/80 mt-1">{comment.quoted.text}</p>
                      </button>
                    )}

                    {isEditing ? (
                      <div className="space-y-2">
                        <TipTapEditor value={editingText} onChange={setEditingText} />
                        <div className="flex justify-end gap-2">
                          <Button variant="ghost" size="sm" onClick={() => setEditingId(null)}>Mégse</Button>
                          <Button size="sm" onClick={() => saveEdit(comment.id)}>Mentés</Button>
                        </div>
                      </div>
                    ) : (
                      <div
                        className="prose prose-md prose-invert max-w-none break-words"
                        dangerouslySetInnerHTML={{ __html: comment.content_html }}
                      />
                    )}

                    {!isEditing && (canReply || canModify) && (
                      <div className="flex justify-end gap-2 mt-3">
                        {canReply && (
                          <Button variant="ghost" size="sm" onClick={() => startQuote(comment)}>
                            <Reply className="h-4 w-4 mr-1" /> Válasz
                          </Button>
                        )}
                        {canModify && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => startEdit(comment)}>
                              <Edit className="h-4 w-4 mr-1" /> Szerkesztés
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => deleteComment(comment.id)}>
                              <Trash2 className="h-4 w-4 mr-1" /> Törlés
                            </Button>
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}

          {request.comments.length === 0 && (
            <p className="text-center text-muted-foreground py-6">Még nincs hozzászólás.</p>
          )}
        </section>

        {/* Válasz írása */}
        <Card id="reply-form" className="bg-card border-border shadow-md">
          <CardContent className="p-6">
            {!isLoggedIn ? (
              <p className="text-center text-muted-foreground">A hozzászóláshoz jelentkezz be.</p>
            ) : isMuted ? (
              <p className="text-center text-muted-foreground">Némítva vagy, nem írhatsz hozzászólást.</p>
            ) : (
              <form onSubmit={handleReply} className="space-y-3">
                {quotedComment && (
                  <div className="flex items-center justify-between gap-2 border-l-4 border-primary bg-secondary/50 rounded p-2 text-sm">
                    <span className="text-muted-foreground">
                      Válasz <span className="text-primary">{quotedComment.user.username}</span> hozzászólására:
                    </span>
                    <Button type="button" variant="ghost" size="sm" onClick={() => setQuotedComment(null)}>
                      <X className="h-4 w-4" />
                    </Button>
                  </div>
                )}
                <TipTapEditor value={replyText} onChange={setReplyText} placeholder="Írd meg a hozzászólásod..." />
                <div className="flex justify-end">
                  <Button type="submit" disabled={submittingReply}>
                    <Send className="h-4 w-4 mr-1" /> {submittingReply ? 'Küldés...' : 'Válasz küldése'}
                  </Button>
                </div>
              </form>
            )}
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
};

export default GameRequestShow;
